import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { formatTime } from '../App';

const LeaderboardScreen = ({ db, appId, isAuthReady, setCurrentScreen, setErrorMessage }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState('hard');

  useEffect(() => {
    if (!db || !isAuthReady || !selectedDifficulty) return;

    const q = query(
      collection(db, 'players'),
      where('completed', '==', true),
      where('difficulty', '==', selectedDifficulty)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const seen = new Map();

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const uniqueKey = `${data.userId}_${data.puzzleId}_${data.difficulty}`;

          // Keep only the best (lowest) time for each uniqueKey
          if (!seen.has(uniqueKey) || data.time < seen.get(uniqueKey).time) {
            seen.set(uniqueKey, { id: doc.id, ...data });
          }
        });

        const uniqueEntries = Array.from(seen.values());
        const sorted = uniqueEntries.sort((a, b) => a.time - b.time);

        setLeaderboardData(sorted);
        setLoadingLeaderboard(false);
      },
      (error) => {
        console.error('Error fetching leaderboard:', error);
        setErrorMessage('Failed to load leaderboard. Please try again.');
        setLoadingLeaderboard(false);
      }
    );

    return () => unsubscribe();
  }, [db, appId, isAuthReady, selectedDifficulty, setErrorMessage]);

  const difficulties = ['hard', 'medium', 'easy'];

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-3xl border-4 border-orange-500 text-center">
        <h2 className="text-4xl font-extrabold mb-4 text-pink-600">Leaderboard</h2>

        <div className="flex space-x-4 mb-6">
          {difficulties.map((level) => (
            <button
              key={level}
              onClick={() => {
                setSelectedDifficulty(level);
                setLoadingLeaderboard(true);
              }}
              className={`px-4 py-2 rounded-lg font-bold text-white transition duration-300 ${
                selectedDifficulty === level
                  ? 'bg-orange-500'
                  : 'bg-pink-400 hover:bg-pink-500'
              }`}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          ))}
        </div>

        {loadingLeaderboard ? (
          <p className="text-gray-700 text-lg">Loading leaderboard...</p>
        ) : leaderboardData.length === 0 ? (
          <p className="text-gray-700 text-lg">No scores yet for {selectedDifficulty} level.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-md">
              <thead>
                <tr className="bg-pink-100 text-gray-700 uppercase text-sm leading-normal">
                  <th className="py-3 px-4 text-left">Rank</th>
                  <th className="py-3 px-4 text-left">Player</th>
                  <th className="py-3 px-4 text-left">Puzzle</th>
                  <th className="py-3 px-4 text-left">Time</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm font-light">
                {leaderboardData.map((entry, index) => (
                  <tr key={entry.id} className="border-b border-gray-200 hover:bg-gray-100">
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="font-bold text-lg text-orange-500">{index + 1}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <img
                          src={entry.pfp}
                          alt={entry.username}
                          className="w-10 h-10 rounded-full mr-3 border-2 border-pink-300"
                        />
                        <span className="font-medium text-base">@{entry.username}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">{entry.puzzleName}</td>
                    <td className="py-3 px-4">{formatTime(entry.time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          onClick={() => setCurrentScreen('selectPuzzle')}
          className="mt-8 bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-pink-600 hover:to-orange-600 transition duration-300 transform hover:scale-105 text-xl"
        >
          Back to Puzzles
        </button>
      </div>
    </div>
  );
};

export default LeaderboardScreen;
